import { forwardRef, ForwardRefRenderFunction, useEffect, useState } from 'react'
import { MenuItem } from '@mui/material'
import { SelectInputProps } from '@mui/material/Select/SelectInput'
import { OptionProps } from 'components'
import moment from 'moment'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CustomPickerModal from './components/CustomPickerModal'

import * as S from './styles'

// TODO: Replace momentjs with dayjs
export interface IntervalPickerProps {
  name: string
  label?: string
  value: string
  defaultValue?: string
  disabled?: boolean
  onChange?: SelectInputProps<unknown>['onChange']
}

const IntervalPickerComponent: ForwardRefRenderFunction<
  HTMLInputElement,
  IntervalPickerProps
> = ({ label, name, value, defaultValue, disabled, onChange }, ref) => {
  const options = [
    {
      title: 'Aberto',
      value: `${moment().startOf('month').format('L')} - ${moment().format('L')}`
    },
    {
      title: 'Últimos 3 meses',
      value: `${moment()
        .subtract(2, 'months')
        .startOf('month')
        .format('L')} - ${moment().format('L')}`
    },
    {
      title: 'Últimos 6 meses',
      value: `${moment()
        .subtract(5, 'months')
        .startOf('month')
        .format('L')} - ${moment().format('L')}`
    },
    {
      title: 'Últimos 12 meses',
      value: `${moment()
        .subtract(11, 'months')
        .startOf('month')
        .format('L')} - ${moment().format('L')}`
    }
  ]
  const defaultCustomInterval = {
    title: 'Personalizado...',
    value: 'custom'
  }

  const [previousValue, setPreviousValue] = useState(options[3].value)
  const [customInterval, setCustomInterval] = useState<OptionProps>(defaultCustomInterval)

  const resetCustomInterval = () => {
    setCustomInterval(defaultCustomInterval)
  }

  useEffect(() => {
    if (!options.some((option) => option.value === value)) {
      const [initialDate, finalDate] = value.split(' - ')
      const startDate = moment(initialDate, 'DD/MM/YYYY')
      const endDate = moment(finalDate, 'DD/MM/YYYY')

      if (startDate.format('MM/YYYY') === endDate.format('MM/YYYY')) {
        setCustomInterval({
          title: `${startDate.format('MM/YY')}`,
          value: `${startDate.format('L')} - ${endDate.format('L')}`
        })
      } else {
        setCustomInterval({
          title: `${startDate.format('MM/YY')} a ${endDate.format('MM/YY')}`,
          value: `${startDate.format('L')} - ${endDate.format('L')}`
        })
      }
    }
  }, [])

  const getValueTitle = (value: string) => {
    if (options.some((option) => option.value === value)) {
      return options.find((option) => option.value === value)?.title
    } else if (customInterval.value === value) {
      return customInterval.title
    }
  }

  return (
    <S.FormControlStyled variant="outlined" fullWidth>
      {!!label && <S.LabelStyled focused={false}>{label}</S.LabelStyled>}

      <S.SelectStyled
        variant="standard"
        disabled={disabled}
        name={name}
        inputRef={ref}
        value={value}
        renderValue={(value) => <span>Mês Cota • {getValueTitle(value as string)}</span>}
        defaultValue={defaultValue}
        onChange={onChange}
        IconComponent={ExpandMoreIcon}
        disableUnderline
        inputProps={{ IconComponent: S.ChevronDownIconStyled }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right'
          },
          PaperProps: {
            sx: {
              '& li': {
                fontSize: '14px'
              }
            }
          }
        }}
      >
        {options.map((option, key) => (
          <MenuItem key={key} value={option.value} onClick={resetCustomInterval}>
            <span>{option.title}</span>
          </MenuItem>
        ))}
        <MenuItem value={customInterval.value} onClick={() => setPreviousValue(value)}>
          <span>{customInterval.title}</span>
        </MenuItem>
        {customInterval.value !== 'custom' && (
          <MenuItem value="custom" onClick={resetCustomInterval}>
            <span>Personalizado...</span>
          </MenuItem>
        )}
      </S.SelectStyled>

      <CustomPickerModal
        fieldName={name}
        open={value === 'custom'}
        previousValue={previousValue}
        setCustomInterval={setCustomInterval}
      />
    </S.FormControlStyled>
  )
}

export const IntervalPicker = forwardRef(IntervalPickerComponent)
